import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, useMatch, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { EVENT_BOTTOM_TAB_IDS } from 'src/routes';
import { useMobileKeyboardOpen } from 'src/utils';
import { EVENT_BOTTOM_NAV_HEIGHT, useFixedElement } from 'src/spacing';
import { useFilteredEventTabs } from 'src/hooks/useFilteredEventTabs';
import { useEventHasMap } from 'src/hooks/useEventHasMap';
import { mapTodoDisabledNavSx } from 'src/theme/mapTodoDisabledNavSx';

export { EVENT_BOTTOM_NAV_HEIGHT };

interface Props {
    eventId: string;
    routePrefix: '/event' | '/viewevent';
}

export default function EventBottomNavigation(props: Props) {
    const { eventId, routePrefix } = props;
    const ref = useFixedElement('bottom');
    const isKeyboardOpen = useMobileKeyboardOpen();
    const [searchParams] = useSearchParams();

    const eventMatch = useMatch('/event/:eventId/:activeTab');
    const viewOnlyEventMatch = useMatch('/viewevent/:eventId/:activeTab');
    const onEventRoute = routePrefix === '/viewevent' ? viewOnlyEventMatch : eventMatch;
    const activeTabId = onEventRoute?.params?.activeTab;
    const theme = useTheme();

    const filteredEventTabs = useFilteredEventTabs(eventId);
    const { hasMap, isLoading: eventMapsLoading } = useEventHasMap(eventId);

    const bottomTabs = useMemo(() => {
        const byId = new Map(filteredEventTabs.map(({ tab }) => [tab.id, tab]));
        return EVENT_BOTTOM_TAB_IDS.map(id => byId.get(id)).filter(Boolean);
    }, [filteredEventTabs]);

    const mapTodoDisabled = !eventMapsLoading && !hasMap;

    const selectedIndex = useMemo(() => {
        const idx = bottomTabs.findIndex(tab => tab?.id === activeTabId);
        return idx >= 0 ? idx : false;
    }, [bottomTabs, activeTabId]);

    if (isKeyboardOpen) return null;
    if (bottomTabs.length === 0) return null;

    const search = searchParams.toString();
    const searchSuffix = search.length > 0 ? `?${search}` : '';

    return (
        <Paper
            ref={ref}
            elevation={3}
            style={{
                position: 'fixed',
                bottom: 0,
                width: '100vw',
                height: EVENT_BOTTOM_NAV_HEIGHT,
                zIndex: 10,
            }}
        >
            <BottomNavigation showLabels value={selectedIndex} sx={{ gap: 0, height: '100%' }}>
                {bottomTabs.map((tab, index) => {
                    if (!tab) return null;
                    const isMapTodo = tab.id === 'map' || tab.id === 'todo';
                    const disabled = mapTodoDisabled && isMapTodo;
                    return (
                        <BottomNavigationAction
                            sx={{
                                minWidth: 60,
                                maxWidth: 80,
                                '& .MuiSvgIcon-root': {
                                    fontSize: '1.25rem',
                                },
                                ...(disabled ? mapTodoDisabledNavSx(theme) : {}),
                            }}
                            key={tab.id}
                            label={tab.label}
                            icon={<tab.icon />}
                            component={disabled ? 'div' : Link}
                            {...(disabled ? {} : { to: `${routePrefix}/${eventId}/${tab.id}${searchSuffix}` })}
                            disabled={disabled}
                            value={index}
                        />
                    );
                })}
            </BottomNavigation>
        </Paper>
    );
}
